/**
 * deep-links.ts — pure deep-link builders for marketplace contact channels
 * (STORY-003-003).
 *
 * One typed module that every contact surface imports so no two surfaces
 * compute a `wa.me` / `t.me` / `tel:` / `dgis://` URL slightly differently
 * and no card ever renders a dead button (REQ-PRD-§11.6-B/C/D). The four
 * consumers are SellerCard (STORY-003-005), SellerDetail (STORY-003-006),
 * the inline seller card in chat (STORY-004-007) and the 2GIS map button
 * (STORY-003-004) — all out of scope here; this story produces the URLs.
 *
 * Contracts:
 *   - PURE FUNCTIONS ONLY. No React hooks (the localised WhatsApp message
 *     is resolved from the shared i18next instance via an explicit `lng`,
 *     not `useTranslation`), so this module is importable from any
 *     component without a side-effect-bearing render dependency.
 *   - HIDDEN-WHEN-NULL (REQ-PRD-§11.6-D). Every builder returns
 *     `string | null` (or a `{ primary, fallback } | null` pair). `null`
 *     — never an empty string — is the canonical "do not render a button"
 *     signal the consumer checks.
 *   - STRICT E.164 (`^\+[1-9]\d{1,14}$`). `wa.me` drops the leading `+`
 *     (`wa.me/996700123456`); `tel:` keeps it (`tel:+996700123456`).
 *   - encodeURIComponent (NOT encodeURI) for the prefilled message, so
 *     Cyrillic + KY-specific ң/ө/ү round-trip (REQ-PRD-§13.5-A).
 *
 * i18n: the `deep-links` namespace is registered here on the shared
 * instance via `addResourceBundle` (the feature-owns-its-namespace pattern
 * established by `registerChatI18n`), which keeps the shared bootstrap
 * `@/lib/i18n` untouched. RU/KY/EN are all first-class (ADR-028); RU is
 * the documented fallback for any missing KY/EN key (REQ-PRD-§13.2-A).
 */
import i18n, { initI18n } from "@/lib/i18n";
import ruDeepLinks from "@/locales/ru/deep-links.json";
import kyDeepLinks from "@/locales/ky/deep-links.json";
import enDeepLinks from "@/locales/en/deep-links.json";
import {
  enqueueAnalyticsEvent,
  type AnalyticsContactChannel,
} from "@/lib/analytics-queue";

/** Active chat / UI language. Trilingual at MVP per ADR-028. */
export type Language = "ru" | "ky" | "en";

/** i18next namespace owned by this module. */
export const DEEP_LINKS_NS = "deep-links" as const;

/** Strict E.164: leading `+`, no leading zero, 2–15 total digits. */
const E164_RE = /^\+[1-9]\d{1,14}$/;

/** WhatsApp caps the prefilled text; keep messages well under this. */
const WHATSAPP_MAX_MESSAGE_LENGTH = 1024;

/**
 * Register the `deep-links` namespace on the shared i18n instance
 * (idempotent). `initI18n()` is idempotent and guarantees the instance is
 * initialised before `addResourceBundle` (the bundle methods only exist
 * post-init). Mirrors `registerChatI18n` so the shared bootstrap
 * (`@/lib/i18n`) stays untouched while `t('…', { ns: 'deep-links' })`
 * resolves.
 */
export function registerDeepLinksI18n(): void {
  initI18n();
  if (!i18n.hasResourceBundle("ru", DEEP_LINKS_NS)) {
    i18n.addResourceBundle("ru", DEEP_LINKS_NS, ruDeepLinks, true, true);
  }
  if (!i18n.hasResourceBundle("ky", DEEP_LINKS_NS)) {
    i18n.addResourceBundle("ky", DEEP_LINKS_NS, kyDeepLinks, true, true);
  }
  if (!i18n.hasResourceBundle("en", DEEP_LINKS_NS)) {
    i18n.addResourceBundle("en", DEEP_LINKS_NS, enDeepLinks, true, true);
  }
}

registerDeepLinksI18n();

/** Trim + validate a candidate phone against strict E.164. */
function normalizeE164(phone: string | null | undefined): string | null {
  if (typeof phone !== "string") {
    return null;
  }
  const trimmed = phone.trim();
  return E164_RE.test(trimmed) ? trimmed : null;
}

/** Which WhatsApp default-message template a surface wants. */
export type WhatsAppContext = "product" | "seller_card" | "inline_chat";

export interface BuildWhatsAppOpts {
  phoneE164: string | null | undefined;
  language: Language;
  /** Defaults to `seller_card`. */
  context?: WhatsAppContext;
  /** Injected into the `product` / `inline_chat` templates when present. */
  productName?: string | null;
  /** Reserved for future templates; accepted for call-site stability. */
  sellerBusinessName?: string | null;
}

/**
 * Resolve the localised WhatsApp default message from the shared i18n
 * instance. Uses an explicit `lng` (pure — no hooks). i18next falls back
 * to RU automatically when a KY/EN key is missing (REQ-PRD-§13.2-A).
 */
function resolveWhatsAppMessage(
  language: Language,
  context: WhatsAppContext,
  productName: string | null | undefined,
): string {
  registerDeepLinksI18n();
  const key = `whatsapp.default.${context}`;
  const interpolation =
    typeof productName === "string" && productName.length > 0
      ? { product_name: productName }
      : undefined;
  // `getFixedT(lng, ns)` resolves against the explicit language with the
  // shared instance's RU fallback chain intact.
  const t = i18n.getFixedT(language, DEEP_LINKS_NS);
  return interpolation ? t(key, interpolation) : t(key);
}

/**
 * Build a WhatsApp deep link: `https://wa.me/{e164-no-plus}?text={msg}`.
 * Returns `null` when the phone is missing or not strict E.164.
 */
export function buildWhatsAppLink(opts: BuildWhatsAppOpts): string | null {
  const e164 = normalizeE164(opts.phoneE164);
  if (e164 === null) {
    return null;
  }
  const context: WhatsAppContext = opts.context ?? "seller_card";
  const message = resolveWhatsAppMessage(
    opts.language,
    context,
    opts.productName,
  ).slice(0, WHATSAPP_MAX_MESSAGE_LENGTH);
  // `wa.me` wants the number without the leading `+`.
  const numberPart = e164.slice(1);
  const text = encodeURIComponent(message);
  return `https://wa.me/${numberPart}?text=${text}`;
}

export interface BuildTelegramOpts {
  /** Accepts `@user` or `user`; the leading `@` is stripped internally. */
  handle: string | null | undefined;
  /** `https` (default — works in every browser) or `tg` (app scheme). */
  preferProtocol?: "tg" | "https";
}

/**
 * Build a Telegram deep link: `https://t.me/{handle}` (default) or
 * `tg://resolve?domain={handle}`. Returns `null` for an empty handle.
 */
export function buildTelegramLink(opts: BuildTelegramOpts): string | null {
  if (typeof opts.handle !== "string") {
    return null;
  }
  const handle = opts.handle.trim().replace(/^@+/, "");
  if (handle.length === 0) {
    return null;
  }
  if (opts.preferProtocol === "tg") {
    return `tg://resolve?domain=${handle}`;
  }
  return `https://t.me/${handle}`;
}

/**
 * Build a phone deep link: `tel:{e164}` (with the leading `+`). Returns
 * `null` when the phone is missing or not strict E.164.
 */
export function buildPhoneLink(phoneE164: string | null | undefined): string | null {
  const e164 = normalizeE164(phoneE164);
  return e164 === null ? null : `tel:${e164}`;
}

export interface Build2GisOpts {
  lat: number | null | undefined;
  lng: number | null | undefined;
  placeName?: string | null;
  /** Override mobile detection. When omitted, the UA is sniffed. */
  preferApp?: boolean;
}

export interface TwoGisLinks {
  /** Attempt first. `dgis://` on mobile, the https web link on desktop. */
  primary: string;
  /** Always the https web link — the fallback when the app scheme fails. */
  fallback: string;
}

/** A finite lat/lng? (rejects null/undefined/NaN/±Infinity). */
function isFiniteCoord(n: number | null | undefined): n is number {
  return typeof n === "number" && Number.isFinite(n);
}

/**
 * Heuristic: is this a mobile user agent? Prefers the structured
 * User-Agent Client Hint (`navigator.userAgentData.mobile`) and falls
 * back to a UA-string regex. Returns `false` in non-DOM contexts.
 */
function isMobileUserAgent(): boolean {
  if (typeof navigator === "undefined") {
    return false;
  }
  const uaData = (
    navigator as Navigator & { userAgentData?: { mobile?: boolean } }
  ).userAgentData;
  if (uaData && typeof uaData.mobile === "boolean") {
    return uaData.mobile;
  }
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

/**
 * Build the 2GIS route links per TRD §9.5.
 *   - primary (mobile): `dgis://routeSearch/rsType/car/to/{lat},{lng}`
 *   - fallback (web):    `https://2gis.kg/routeSearch/rsType/car/to/{lat},{lng}`
 *
 * On desktop, `primary` is the web link too (no app to hand off to). The
 * consumer attempts `primary` and, per the iOS-Safari universal-link
 * caveat, falls back to `fallback` via a timeout-or-error UI hint — that
 * dance is a component concern (STORY-003-004), not this helper's.
 *
 * Returns `null` when either coordinate is missing/non-finite.
 */
export function build2GisLinks(opts: Build2GisOpts): TwoGisLinks | null {
  if (!isFiniteCoord(opts.lat) || !isFiniteCoord(opts.lng)) {
    return null;
  }
  const path = `routeSearch/rsType/car/to/${opts.lat},${opts.lng}`;
  const fallback = `https://2gis.kg/${path}`;
  const useApp = opts.preferApp ?? isMobileUserAgent();
  const primary = useApp ? `dgis://${path}` : fallback;
  return { primary, fallback };
}

/**
 * Record a first-party "contact click" analytics event before the
 * consumer navigates. PII-FREE by construction: only the channel enum and
 * opaque seller/product IDs are recorded — never the phone number, handle
 * or address (REQ-TRD-§14.6-A). No-op until EPIC-009 attaches a drainer
 * to `window.__gardenAnalyticsQueue` (and a no-op outside a DOM context).
 *
 * Call this from inside the click handler — NOT the render path — so React
 * 19 strict mode does not double-invoke it.
 */
export function trackContactClick(
  channel: AnalyticsContactChannel,
  sellerId: string,
  productId?: string,
): void {
  const props =
    productId === undefined
      ? { channel, sellerId }
      : { channel, sellerId, productId };
  enqueueAnalyticsEvent("marketplace.contact_click", props);
}
